/**
 * TCI-RS 결과지 읽기 (A35). PDF에서 꺼낸 글자 조각(위치 포함)을 받아 점수 표를 만든다 (순수 함수).
 * 결과지의 이름·성별·나이·고유번호는 읽지 않는다. 검사일·규준집단·무응답 수와 점수만.
 */
import type { AssessmentScore } from '../schema/v1';

export type PdfTextItem = { x: number; y: number; str: string };

/** 주 척도 (기질 4 · 성격 3 · SC) */
export const TCI_SCALES: { key: string; label: string; group: '기질' | '성격' | '합' }[] = [
  { key: 'NS', label: '자극추구', group: '기질' },
  { key: 'HA', label: '위험회피', group: '기질' },
  { key: 'RD', label: '사회적 민감성', group: '기질' },
  { key: 'PS', label: '인내력', group: '기질' },
  { key: 'SD', label: '자율성', group: '성격' },
  { key: 'CO', label: '연대감', group: '성격' },
  { key: 'ST', label: '자기초월', group: '성격' },
  { key: 'SC', label: '자율성+연대감', group: '합' },
];

/** 하위척도: 높은 쪽 이름 / 낮은 쪽 이름 */
export const TCI_SUBSCALES: Record<string, { high: string; low: string }> = {
  NS1: { high: '탐색적 흥분', low: '관습적 안정성' },
  NS2: { high: '충동성', low: '심사숙고' },
  NS3: { high: '무절제', low: '절제' },
  NS4: { high: '자유분방', low: '질서정연' },
  HA1: { high: '예기불안', low: '낙천성' },
  HA2: { high: '불확실성에 대한 두려움(높음)', low: '불확실성에 대한 두려움(낮음)' },
  HA3: { high: '낯선 사람에 대한 수줍음(높음)', low: '낯선 사람에 대한 수줍음(낮음)' },
  HA4: { high: '쉽게 지침', low: '활기 넘침' },
  RD1: { high: '정서적 감수성(높음)', low: '정서적 감수성(낮음)' },
  RD2: { high: '정서적 개방성(높음)', low: '정서적 개방성(낮음)' },
  RD3: { high: '친밀감', low: '거리두기' },
  RD4: { high: '의존', low: '독립' },
  PS1: { high: '근면(높음)', low: '근면(낮음)' },
  PS2: { high: '끈기(높음)', low: '끈기(낮음)' },
  PS3: { high: '성취에 대한 야망(높음)', low: '성취에 대한 야망(낮음)' },
  PS4: { high: '완벽주의(높음)', low: '완벽주의(낮음)' },
  SD1: { high: '책임감(높음)', low: '책임감(낮음)' },
  SD2: { high: '목적의식(높음)', low: '목적의식(낮음)' },
  SD3: { high: '유능감(높음)', low: '유능감(낮음)' },
  SD4: { high: '자기수용(높음)', low: '자기수용(낮음)' },
  SD5: { high: '자기일치(높음)', low: '자기일치(낮음)' },
  CO1: { high: '타인수용(높음)', low: '타인수용(낮음)' },
  CO2: { high: '공감(높음)', low: '공감(낮음)' },
  CO3: { high: '이타성(높음)', low: '이타성(낮음)' },
  CO4: { high: '관대함(높음)', low: '관대함(낮음)' },
  CO5: { high: '공평(높음)', low: '공평(낮음)' },
  ST1: { high: '창조적 자기망각', low: '자의식' },
  ST2: { high: '우주만물과의 일체감(높음)', low: '우주만물과의 일체감(낮음)' },
  ST3: { high: '영성 수용', low: '합리적 유물론' },
};

export type TciResult = {
  testName: string;
  administeredAt?: string;
  normGroup?: string;
  missingResponses?: number;
  scores: AssessmentScore[];
};

/** 같은 높이의 조각을 한 줄로 (왼쪽부터) */
export function linesOf(items: PdfTextItem[]): string[] {
  const rows: { y: number; parts: PdfTextItem[] }[] = [];
  for (const it of items) {
    if (!it.str.trim()) continue;
    const row = rows.find((r) => Math.abs(r.y - it.y) <= 2);
    if (row) row.parts.push(it);
    else rows.push({ y: it.y, parts: [it] });
  }
  return rows
    .sort((a, b) => b.y - a.y)
    .map((r) =>
      r.parts
        .sort((a, b) => a.x - b.x)
        .map((p) => p.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim(),
    );
}

export function isTciReport(pages: PdfTextItem[][]): boolean {
  const tight = pages.flatMap(linesOf).join('').replace(/\s+/g, '');
  return /TCI-RS/.test(tight) && /(자극추구|기질및성격)/.test(tight);
}

const num = (s: string) => Number(s);

/** 결과지 → 점수 표. 주 척도 7개 이상을 못 읽으면 오류 */
export function parseTciReport(pages: PdfTextItem[][]): { ok: true; value: TciResult } | { ok: false; error: string } {
  const lines = pages.flatMap(linesOf);
  const tight = lines.join('\n').replace(/[ \t]+/g, '');
  if (!isTciReport(pages)) return { ok: false, error: 'TCI 결과지가 아닙니다' };

  const date = /검사일:?(\d{4})(\d{2})(\d{2})/.exec(tight);
  const norm = /규준집단:?([^\n]+?)무응답수/.exec(tight);
  const missing = /무응답수:?(\d+)/.exec(tight);
  const kind = tight.includes('성인용') ? ' 성인용' : tight.includes('청소년') ? ' 청소년용' : tight.includes('아동') ? ' 아동용' : '';

  const scores: AssessmentScore[] = [];
  for (const line of lines) {
    const m = /\((NS|HA|RD|PS|SD|CO|ST|SC)\)\s+(\d+)\s+(\d+)\s+(\d+)\b/.exec(line);
    if (m && !scores.some((s) => s.key === m[1])) {
      const def = TCI_SCALES.find((d) => d.key === m[1])!;
      scores.push({ key: m[1]!, label: def.label, level: 'scale', raw: num(m[2]!), t: num(m[3]!), percentile: num(m[4]!) });
    }
  }
  for (const line of lines) {
    const m = /\b(NS[1-4]|HA[1-4]|RD[1-4]|PS[1-4]|SD[1-5]|CO[1-5]|ST[1-3])\s+(\d+)\s+(\d+(?:\.\d+)?)\s*\(\s*(\d+(?:\.\d+)?)\s*\)/.exec(line);
    if (m && !scores.some((s) => s.key === m[1])) {
      const d = TCI_SUBSCALES[m[1]!]!;
      scores.push({ key: m[1]!, label: `${d.high} ↔ ${d.low}`, level: 'subscale', raw: num(m[2]!), mean: num(m[3]!), sd: num(m[4]!) });
    }
  }
  const main = scores.filter((s) => s.level === 'scale');
  if (main.length < 7) return { ok: false, error: `주 척도를 ${main.length}개만 읽었습니다 (7개 필요). 결과지 형식을 확인하세요` };
  return {
    ok: true,
    value: {
      testName: `TCI-RS${kind}`,
      ...(date ? { administeredAt: `${date[1]}-${date[2]}-${date[3]}` } : {}),
      ...(norm ? { normGroup: norm[1]!.trim() } : {}),
      ...(missing ? { missingResponses: Number(missing[1]) } : {}),
      scores,
    },
  };
}

/** 백분위 30 이하 낮음, 70 이상 높음 (결과지 기준) */
export function scaleLevel(percentile: number | undefined): '높음' | '낮음' | '보통' | undefined {
  if (percentile === undefined) return undefined;
  return percentile >= 70 ? '높음' : percentile <= 30 ? '낮음' : '보통';
}

/** 하위척도: 규준 평균 ± 1 표준편차 밖이면 높음/낮음 (참고자료) */
export function subscaleLevel(s: AssessmentScore): '높음' | '낮음' | '보통' | undefined {
  if (s.raw === undefined || s.mean === undefined || s.sd === undefined) return undefined;
  return s.raw >= s.mean + s.sd ? '높음' : s.raw <= s.mean - s.sd ? '낮음' : '보통';
}

/** 문서·자료에 들어가는 점수 요약 한 단락 */
export function tciSummary(scores: AssessmentScore[]): string {
  const main = scores.filter((s) => s.level === 'scale');
  const part = (keys: string[]) =>
    main
      .filter((s) => keys.includes(s.key))
      .map((s) => `${s.label}(${s.key}) T${s.t ?? '-'}·백분위 ${s.percentile ?? '-'}${scaleLevel(s.percentile) && scaleLevel(s.percentile) !== '보통' ? `(${scaleLevel(s.percentile)})` : ''}`)
      .join(', ');
  const subs = scores
    .filter((s) => s.level === 'subscale')
    .map((s) => ({ s, lv: subscaleLevel(s) }))
    .filter((x) => x.lv === '높음' || x.lv === '낮음')
    .map(({ s, lv }) => `${s.key} ${lv === '높음' ? TCI_SUBSCALES[s.key]?.high : TCI_SUBSCALES[s.key]?.low}`);
  return [
    `기질: ${part(['NS', 'HA', 'RD', 'PS'])}`,
    `성격: ${part(['SD', 'CO', 'ST'])}`,
    main.find((s) => s.key === 'SC') ? `자율성+연대감(SC) T${main.find((s) => s.key === 'SC')!.t}` : '',
    subs.length ? `두드러진 하위척도(참고): ${subs.join(', ')}` : '',
  ]
    .filter(Boolean)
    .join(' / ');
}
